import {
  Box,
  FormHelperText,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  CSSProperties,
} from "react";
import { Controller, useController, useFormContext } from "react-hook-form";
import Grow from "@mui/material/Grow";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import { UploadIcon } from "components/Layout/MainLayout/Sidebar/icons/UploadIcon";
import { useIntl } from "react-intl";


// Component props

interface IProps {
  image: string | undefined;
  name: string;
  cssImage: CSSProperties;
  height?: string;
  iconColor?: string;
  borderColor?: string;
  letterColor?: string;
  isCircle?: boolean;
}


export const FileUpload: React.FC<IProps> = (props) => {
  const { formatMessage } = useIntl();
  const UPLOAD_IMAGE_LABEL = formatMessage({ id: "company.add.photo" });

  const { name, cssImage, height, iconColor = "#718096", borderColor = "#D0D5DD", letterColor = "#64748B", isCircle } = props

  const { control, formState: { isSubmitting, errors } } = useFormContext();
  const { field } = useController({ name, control });

  const [singleFile, setSingleFile] = useState<File[]>(
    field.value ? [field.value] : []
  );
  const wrapperRef = useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (field.value && singleFile.length === 0) {
      setSingleFile([field.value]);
    }
  }, [field.value, singleFile]);

  const onDragEnter = () => wrapperRef.current?.classList.add("dragover");
  const onDragLeave = () => wrapperRef.current?.classList.remove("dragover");

  const onFileDrop = useCallback(
    (e: React.SyntheticEvent<EventTarget>) => {
      const target = e.target as HTMLInputElement;
      if (!target.files) return;

      const newFile = Object.values(target.files).map((file: File) => file);
      if (singleFile.length >= 1) return alert("Only a single image allowed");
      const updatedSingleList = [...singleFile, ...newFile];
      setSingleFile(updatedSingleList);
      field.onChange(updatedSingleList[0]);
    },
    [field, singleFile]
  );

  const fileSingleRemove = (file: File) => {
    field.onChange("");
    const updatedList = [...singleFile];
    updatedList.splice(singleFile.indexOf(file), 1);
    setSingleFile(updatedList);
  };

  useEffect(() => {
    if (isSubmitting) {
      setSingleFile([]);
    }
  }, [isSubmitting]);

  return (
    <Box >
      {singleFile.length < 1 && (
        <Box>
          <Grow in={true}>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{
                position: "relative",
                width: `${!isCircle && "100%"}`,
                height: height || "100%",
                border: `1px solid ${borderColor}`,
                borderRadius: !isCircle ? "5px" : "50%",
                padding: `${isCircle && "1.5rem"}`
              }}
              ref={wrapperRef}
              onDragEnter={onDragEnter}
              onDragLeave={onDragLeave}
              onDrop={onDragLeave}
            >

              <Stack justifyContent="center" sx={{ p: 1, textAlign: "center" }}>
                <div>
                  <UploadIcon
                    sx={{ fontSize: 60, fill: iconColor }}
                  />
                </div>
                <Typography
                  color={letterColor}
                  fontSize={"14px"}
                  fontWeight={400}
                >
                  {UPLOAD_IMAGE_LABEL}
                </Typography>
              </Stack>

              <Controller
                name={name}
                defaultValue={""}
                control={control}
                render={({ field: { name, onBlur, ref } }) => (
                  <input
                    type="file"
                    name={name}
                    onBlur={onBlur}
                    ref={ref}
                    onChange={onFileDrop}
                    multiple={false}
                    accept="image/jpg, image/png, image/jpeg"
                    style={{
                      opacity: 0,
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      cursor: "pointer",
                    }}
                  />
                )}
              />
            </Box>
          </Grow>
          {errors[name] &&
            <FormHelperText
              sx={{ textAlign: "center" }}
              error={!!errors[name]}
            >
              {(errors[name]?.message as unknown as string)}
            </FormHelperText>}
        </Box>
      )}
      {singleFile.length > 0 ? (
        <>
          {singleFile.map((item, index) => {
            const stringIndex: string = index.toString();
            return (
              <Box
                key={stringIndex + "-box1"}
                sx={{
                  position: "relative",
                  backgroundColor: "#f6f6f6",
                  borderRadius: 1.5,
                }}
              >
                <Box display="flex" key={stringIndex + "-box2"}>
                  <img
                    src={URL.createObjectURL(item)}
                    alt="upload"
                    style={cssImage}
                  />
                </Box>
                <IconButton
                  key={stringIndex + "-iconButton"}
                  onClick={() => fileSingleRemove(item)}
                  sx={{
                    position: "absolute",
                    right: "1rem",
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                >
                  <DeleteIcon key={stringIndex + "-deleteIcon"} />
                </IconButton>
              </Box>
            );
          })}
        </>
      ) : null}
    </Box>
  );
};
